import { Package } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Skeleton } from '~/components/ui/skeleton';
import type {
  ProductViewDensity,
  SyncCapability,
} from '~/features/explorer/types/products-catalog.types';
import { isCapabilitySupported } from '~/features/explorer/utils/products-catalog';
import { cn } from '~/utils/cn';

import { ProductCard } from './product-card';

type Product = Record<string, unknown>;

/** Responsive column classes per density preset. Comfort (default) mirrors the
 *  Catalog Browse reference; compact packs more, spacious gives cards room. */
const DENSITY_GRID_CLASS: Record<ProductViewDensity, string> = {
  compact: 'grid-cols-3 sm:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6',
  comfort: 'grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5',
  spacious: 'grid-cols-2 sm:grid-cols-3 lg:grid-cols-4',
};

/** Stable React key for a product tile: first primitive id alias, else index. */
function rowKey(p: Product, index: number): string {
  for (const v of [p.id, p.externalId, p.offer_id, p.offerId]) {
    if (typeof v === 'string' && v) return v;
    if (typeof v === 'number') return String(v);
  }
  return String(index);
}

interface ProductGridProps {
  products: Product[];
  capabilities: SyncCapability[];
  isLoading: boolean;
  isFetching: boolean;
  onEdit: (product: Product) => void;
  onDelete: (product: Product) => void;
  onRowClick: (product: Product) => void;
  /** Grid density preset (Catalog Browse layout). When set it wins over
   *  `gridClassName`; when omitted the grid falls back to `gridClassName` or the
   *  legacy 2/3/4-column layout so other consumers are unaffected. */
  density?: ProductViewDensity;
  /** Append placeholder skeleton cards after the products while the next page
   *  loads (infinite scroll) — reuses the same skeleton as the initial load. */
  loadingMore?: boolean;
  /** Override the responsive column breakpoints (used when `density` is unset). */
  gridClassName?: string;
}

/** Legacy responsive columns for consumers that don't opt into density. */
const LEGACY_GRID_CLASS = 'grid-cols-2 sm:grid-cols-3 lg:grid-cols-4';

/** Placeholder card shown while the first page of products loads. */
function ProductCardSkeleton() {
  return (
    <div className="bg-card overflow-hidden rounded-xl border shadow-sm">
      <Skeleton className="aspect-[4/3] w-full rounded-none" />
      <div className="space-y-2.5 p-3">
        <Skeleton className="h-3.5 w-full" />
        <Skeleton className="h-3 w-3/5" />
        <div className="flex gap-1">
          <Skeleton className="h-4 w-14 rounded-md" />
          <Skeleton className="h-4 w-10 rounded-md" />
        </div>
        <div className="border-border/50 flex items-center justify-between border-t pt-1">
          <Skeleton className="h-4 w-14" />
          <Skeleton className="h-3 w-16" />
        </div>
      </div>
    </div>
  );
}

/**
 * Responsive card grid for the products list (2/3/4 columns by default;
 * overridable via `gridClassName`). Shows 8 skeleton cards while loading, a
 * dashed empty state, and dims to opacity-60 while a page refetch is in flight.
 */
export function ProductGrid({
  products,
  capabilities,
  isLoading,
  isFetching,
  onEdit,
  onDelete,
  onRowClick,
  density,
  loadingMore = false,
  gridClassName,
}: ProductGridProps) {
  const { t } = useTranslation('explorer');
  const canEdit = isCapabilitySupported(capabilities, 'update');
  const canDelete = isCapabilitySupported(capabilities, 'delete');
  const gridClass = density ? DENSITY_GRID_CLASS[density] : (gridClassName ?? LEGACY_GRID_CLASS);

  if (isLoading) {
    return (
      <div className={cn('grid gap-4', gridClass)}>
        {Array.from({ length: density ? 12 : 8 }).map((_, i) => (
          <ProductCardSkeleton key={i} />
        ))}
      </div>
    );
  }

  if (products.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center rounded-xl border border-dashed py-16 text-center">
        <div className="bg-muted mb-3 rounded-full p-4">
          <Package className="text-muted-foreground/40 h-7 w-7" />
        </div>
        <p className="text-sm font-medium">{t('products.noProducts')}</p>
        <p className="text-muted-foreground mt-1 text-xs">{t('products.adjustFilters')}</p>
      </div>
    );
  }

  return (
    <div
      className={cn(
        'grid gap-4 transition-opacity duration-200',
        gridClass,
        isFetching && 'opacity-60',
      )}
    >
      {products.map((p, i) => (
        <ProductCard
          key={rowKey(p, i)}
          product={p}
          canEdit={canEdit}
          canDelete={canDelete}
          onView={() => {
            onRowClick(p);
          }}
          onEdit={() => {
            onEdit(p);
          }}
          onDelete={() => {
            onDelete(p);
          }}
        />
      ))}
      {/* Append skeletons in the same grid while the next page loads. */}
      {loadingMore &&
        Array.from({ length: density ? 6 : 4 }).map((_, i) => (
          <ProductCardSkeleton key={`more-${String(i)}`} />
        ))}
    </div>
  );
}
